import { Helmet } from 'react-helmet-async';

export default function SEO({ title, description, canonical, image, article, schema }) {
  const siteName = 'Ottica Bellodi Torino';
  const defaultImage = '/og-ottica-bellodi.jpg';
  const baseUrl = 'https://www.otticabellodi.it';

  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const ogImage = image || defaultImage;
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;
  const canonicalUrl = canonical ? `${baseUrl}${canonical}` : null;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Optician",
    "name": "Ottica Bellodi",
    "image": ogImageUrl,
    "@id": baseUrl,
    "url": baseUrl,
    "telephone": "011396871",
    "description": "Ottica Bellodi: centro ottico a Torino fondato nel 1955. Specializzati in misurazione vista, lenti progressive personalizzate e montature stampate 3D.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Corso Giovanni Agnelli, 104/B",
      "addressLocality": "Torino",
      "postalCode": "10137",
      "addressCountry": "IT",
      "addressRegion": "TO"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 45.0366498,
      "longitude": 7.6439308
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "12:30"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "15:30",
        "closes": "19:30"
      }
    ]
  };

  const blogPostingSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": description || '',
    "image": article.image ? (article.image.startsWith('http') ? article.image : `${baseUrl}${article.image}`) : ogImageUrl,
    "url": canonicalUrl || baseUrl,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Organization",
      "name": "Ottica Bellodi",
      "url": baseUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "Ottica Bellodi",
      "url": baseUrl,
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo-ottica-bellodi.svg`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl || baseUrl
    }
  } : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* OpenGraph */}
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="it_IT" />
      {article?.date && <meta property="article:published_time" content={article.date} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImageUrl} />

      {/* JSON-LD LocalBusiness */}
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>

      {/* JSON-LD BlogPosting (solo articoli) */}
      {blogPostingSchema && (
        <script type="application/ld+json">{JSON.stringify(blogPostingSchema)}</script>
      )}

      {/* JSON-LD schema aggiuntivo per pagina */}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}
